import { 
  ObservationEditProto, 
  ObservationEditType, 
  KGObservationProto 
} from "build-ts/analysis_plan";
import { MetadataTypeEnum } from "build-ts/enums";
import { 
  ObservationColumnProto,
  Header,
  MetricProto
} from "build-ts/metadata";
import { v4 as uuidv4 } from "uuid";

export class ObservationUtils {
  /**
   * @returns Observation columns.
   */
  public static getObservationColumns(headers: Header[]): ObservationColumnProto[] {
    return (
      headers?.map((header) => {
        if (header.type === MetadataTypeEnum.METRIC) {
          return ObservationColumnProto.create({
            column_id: header?.id,
            kg_column_header: header,
          });
        }
        return ObservationColumnProto.create({
          column_id: uuidv4(),
          kg_column_header: header,
        });
      })
      .filter(Boolean) as ObservationColumnProto[]
    );
  }

  /**
   * @returns The observation edits corresponding to the given parent and child KGObservationProtos.
   */
  public static getObservationEdits(
    parent: KGObservationProto,
    child: KGObservationProto
  ): ObservationEditProto[] {
    const edits: ObservationEditProto[] = [];

    // COLUMNS
    child.columns?.forEach((c) => {
      if (!parent.columns?.some((p) => p.kg_column_header?.id === c.kg_column_header?.id)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.ADD_COLUMN,
            column: c,
          })
        );
      }
    });
    parent.columns?.forEach((p) => {
      if (!child.columns?.some((c) => c.kg_column_header?.id === p.kg_column_header?.id)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.REMOVE_COLUMN,
            column: p,
          })
        );
      }
    });

    // FILTERS
    child.filters?.forEach((childF) => {
      const str = JSON.stringify(childF);
      if (!parent.filters?.some((pf) => JSON.stringify(pf) === str)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.ADD_FILTER,
            filter: childF,
          })
        );
      }
    });
    parent.filters?.forEach((parentF) => {
      const str = JSON.stringify(parentF);
      if (!child.filters?.some((cf) => JSON.stringify(cf) === str)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.REMOVE_FILTER,
            filter: parentF,
          })
        );
      }
    });

    // BENCHMARKS
    child.benchmarks?.forEach((cB) => {
      if (!parent.benchmarks?.some((pB) => pB.header?.id === cB.header?.id)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.ADD_BENCHMARK,
            benchmark: cB,
          })
        );
      }
    });
    parent.benchmarks?.forEach((pB) => {
      if (!child.benchmarks?.some((cB) => cB.header?.id === pB.header?.id)) {
        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.REMOVE_BENCHMARK,
            benchmark: pB,
          })
        );
      }
    });

    // METRICS
    child.metrics?.forEach((cM) => {
      if (!parent.metrics?.some((pM) => pM.header?.id === cM.header?.id)) {
        // Create a new MetricProto to ensure the header is preserved properly
        const metricWithHeader = MetricProto.create({
          header: cM.header ? { ...cM.header } : undefined
        });

        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.ADD_METRIC,
            metric: metricWithHeader,
          })
        );
      }
    });
    parent.metrics?.forEach((pM) => {
      if (!child.metrics?.some((cM) => cM.header?.id === pM.header?.id)) {
        // Create a new MetricProto to ensure the header is preserved properly
        const metricWithHeader = MetricProto.create({
          header: pM.header ? { ...pM.header } : undefined
        });

        edits.push(
          ObservationEditProto.create({
            edit_type: ObservationEditType.REMOVE_METRIC,
            metric: metricWithHeader,
          })
        );
      }
    });

    return edits;
  }

  /**
   * @returns The observation with the edits applied.
   */
  public static applyRecommendationEdits(kgObservation: KGObservationProto, edits: ObservationEditProto[]): KGObservationProto {
      for (const edit of edits) {
        switch (edit?.edit_type) {
          case ObservationEditType.ADD_COLUMN:
            kgObservation.columns?.push(edit?.column!);
            break;
          case ObservationEditType.REMOVE_COLUMN:
            kgObservation.columns = kgObservation.columns?.filter((c) => c.column_id !== edit?.column?.column_id);
            break;
          case ObservationEditType.ADD_FILTER:
            kgObservation.filters?.push(edit?.filter!);
            break;
          case ObservationEditType.REMOVE_FILTER:
            kgObservation.filters = kgObservation.filters?.filter((f) => f.kg_column_header?.id !== edit?.filter?.kg_column_header?.id);
            break;
          case ObservationEditType.ADD_BENCHMARK:
            kgObservation.benchmarks?.push(edit?.benchmark!);
            break;
          case ObservationEditType.REMOVE_BENCHMARK:
            kgObservation.benchmarks = kgObservation.benchmarks?.filter((b) => b.header?.id !== edit?.benchmark?.header?.id);
            break;
          case ObservationEditType.ADD_METRIC:
            kgObservation.metrics?.push(edit?.metric!);
            break;
          case ObservationEditType.REMOVE_METRIC:
            kgObservation.metrics = kgObservation.metrics?.filter((m) => m.header?.id !== edit?.metric?.header?.id);
            break;
        }
      }
      return kgObservation;
  }
}
